import { Injectable, QueryList } from '@angular/core';
import { NgbPopover, NgbPopoverConfig } from '@ng-bootstrap/ng-bootstrap';
import { BehaviorSubject, Subject, Subscription } from 'rxjs';
import { tap } from 'rxjs/operators';
import { AssistantBehaviourEvent, AssistantEventActionEnum } from '../models/assistant/assistant.behaviour.event';
import { AssistantStatusEvent, AssistantStatusEventActionEnum } from '../models/assistant/assistant.status.event';

export interface FocusedInputField {
  field: HTMLElement;
  popoverForField: NgbPopover;
}

@Injectable()
export class AssistantService {

  private lastFocusedFormField: FocusedInputField = undefined;
  assistantStatusUpdate$: Subject<AssistantStatusEvent> = new BehaviorSubject<AssistantStatusEvent>(undefined);
  assistantBehaviourUpdate$: Subject<AssistantBehaviourEvent> = new Subject<AssistantBehaviourEvent>();
  private lastAssistantStatusEvent: AssistantStatusEvent;
  private subscriptionsOnPopoverShownEvent: Subscription[] = [];

  constructor(private ngpPopoverConfig: NgbPopoverConfig) {
  }

  enable():void{
    this.assistantStatusUpdate$.next({status: AssistantStatusEventActionEnum.ENABLED});
  }

  disable() : void{
    this.assistantStatusUpdate$.next({status: AssistantStatusEventActionEnum.DISABLED});
  }

  closeOthers(currentPopover: NgbPopover) : void{
    this.assistantBehaviourUpdate$.next({action: AssistantEventActionEnum.CLOSE_OTHERS, currentPopover: currentPopover});
  }


  disableAssistant(componentPopovers: QueryList<NgbPopover>) {
    if (componentPopovers) {
      componentPopovers.forEach(
        (item: NgbPopover, index: number, array: NgbPopover[]) => {
          item.disablePopover = true;
          item.close();
        }
      )
    }
  }

  enableAssistant(componentPopovers: QueryList<NgbPopover>, assistantPopoverListChanged: boolean) {
    if (componentPopovers) {
      componentPopovers.forEach(
        (item: NgbPopover, index: number, array: NgbPopover[]) => {
          item.disablePopover = false;

          if (this.lastFocusedFormField && this.lastFocusedFormField.popoverForField === item && assistantPopoverListChanged !== true) {

            /* Das close() erfolgt hier deshalb, da ggf. schon das Popover offen ist und dann nochmal auf den "Assistent starten" Button geklickt wird.
             In diesem Fall würde das Feld erneut fokusiert und der Assistent würde - da er schon geöffnet ist - geschlossen */
            item.close();

            /* Falls wir beim Durchlaufen der Popover-Liste gerade bei dem Popover für das zuletzt fokussierte
               Eingabefeld sind, dann dieses Feld fokussieren.
             */
            this.lastFocusedFormField.field.focus();
          }
        }
      )
    }
  }

  setLastFocusedFormField(lastFocusedFormField: FocusedInputField) {
    this.lastFocusedFormField = lastFocusedFormField;
  }

  getLastFocusedFormField() {
    return this.lastFocusedFormField;
  }

  handleAssistantStatusEventForComponent(event: AssistantStatusEvent, popoverList: QueryList<NgbPopover>) {
    if (event) {
      if (event.status === AssistantStatusEventActionEnum.ENABLED) {
        this.enableAssistant(popoverList, event.assistantPopoverListChanged);
      } else if (event.status === AssistantStatusEventActionEnum.DISABLED) {
        this.disableAssistant(popoverList);
      }
    }
  }

  handleAssistantBehaviourEventForComponent(event: AssistantBehaviourEvent, popoverList: QueryList<NgbPopover>) {
    if (event) {
      if (event.action === AssistantEventActionEnum.CLOSE_OTHERS) {
        if (popoverList) {
          popoverList.forEach(
            (item: NgbPopover, index: number, array: NgbPopover[]) => {
              if (item !== event.currentPopover) {
                item.close();
              }
            }
          )
        }
      }
    }
  }

  initializeAssistant(assistantPopoverList: QueryList<NgbPopover>) {

    this.assistantStatusUpdate$.pipe(
      tap(event => {
        if (event) {
          this.handleAssistantStatusEventForComponent(event, assistantPopoverList);
          this.lastAssistantStatusEvent = Object.assign({}, event);
          this.lastAssistantStatusEvent.assistantPopoverListChanged = true;
        }
      })).subscribe();

    this.assistantBehaviourUpdate$.subscribe((event: AssistantBehaviourEvent) => {
      if (event) {
        this.handleAssistantBehaviourEventForComponent(event, assistantPopoverList);
      }
    });

    this.subscribeToPopoverShownEvent(this.subscriptionsOnPopoverShownEvent, assistantPopoverList);

    assistantPopoverList.changes.subscribe((popoverQueryList: QueryList<NgbPopover>) => {
      /* Bei Änderungen an den vorhandenen Assistenten-Feldern (Ein- und Ausblenden von Bereichen) für alle Felder nochmal den Assistenten ein- oder ausschalten.
       Allerdings sollten wir nicht bei beliebigen Änderungen nochmal den Status aktualisieren, da auch z.B. das Einschalten der Assistenten eine Änderung ist
       und wir sonst öfter als gewollt durch diesen Änderungscode laufen.
       */
      this.handleAssistantStatusEventForComponent(this.lastAssistantStatusEvent, popoverQueryList);
      /* Auch für das neu Subscriben auf das Event "shown" gehen wir davon aus, dass wir das nur dann brauchen, wenn sich die Anzahl
       der Popovers ändert.
       */
      this.subscribeToPopoverShownEvent(this.subscriptionsOnPopoverShownEvent, assistantPopoverList);
    });
  }

  subscribeToPopoverShownEvent(subscriptionsOnPopoverShownEvent: Subscription[], assistantPopoverList: QueryList<NgbPopover>) {

    subscriptionsOnPopoverShownEvent.forEach((value) => {
      value.unsubscribe();
    });

    subscriptionsOnPopoverShownEvent.splice(0, subscriptionsOnPopoverShownEvent.length);

    assistantPopoverList.forEach(
      (item: NgbPopover) => {
        subscriptionsOnPopoverShownEvent.push(item.shown.subscribe((next) => {
          this.closeOthers(item);
        }));
      });
  }
}
